import React, { useState, useMemo, useEffect } from "react";
import AppointmentFormModal from "./AppointmentFormModal";
import StructuralBackButton from "../shared/StructuralBackButton";
import AdminTasksList from "./AdminTasksList";
import { CustomIcon } from "../shared/CustomIcon";
import { _apiGet } from "../../lib/apiClient";
import {
  apiCreateAppointment,
  apiUpdateAppointment,
  apiSetAppointmentStatus,
  apiDeleteAppointment,
} from "./api";

// AppointmentsPage — master overview & management of appointments.
// (Was AppointmentsManagePage before extraction — same routes and behavior.)
export default function AppointmentsPage({ currentUser, isSuperAdmin, appointments, setAppointments, users, navigate }) {
  const [showForm, setShowForm] = useState(false);
  const [editAppt, setEditAppt] = useState(null);
  const [prefillDate, setPrefillDate] = useState("");
  const [filter, setFilter] = useState("upcoming"); // upcoming | all | mine | inbound | admin-tasks
  const [pastorAssistantsById, setPastorAssistantsById] = useState({});

  // Auto-open the New Appointment modal when arriving with ?new=1
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("new") === "1") {
      const d = sp.get("date") || "";
      setEditAppt(null);
      setPrefillDate(d);
      setShowForm(true);
      sp.delete("new"); sp.delete("date");
      const qs = sp.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${qs ? "?" + qs : ""}`);
    }
    // Honour deep links from notifications: ?tab=admin-tasks
    if (sp.get("tab") === "admin-tasks") setFilter("admin-tasks");
  }, []);

  // Load pastor → assistant mapping (admin / pastor only — others won't need it).
  useEffect(() => {
    if (!(isSuperAdmin || currentUser.role === "pastor" || currentUser.role === "limited_admin")) return;
    (async () => {
      try {
        const rows = await _apiGet("/admin/pastors-directory");
        const map = {};
        for (const p of (rows || [])) {
          map[p.id] = (p.assistantIds || [])
            .map(aid => users.find(u => u.id === aid))
            .filter(Boolean);
        }
        setPastorAssistantsById(map);
      } catch (e) { /* swallow — assistants list will appear empty */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, currentUser.role, users.length]);


  const visible = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return appointments
      .filter(a => {
        if (filter === "upcoming") return !a.date || new Date(a.date) >= today;
        if (filter === "mine") return a.userId === currentUser.id;
        if (filter === "inbound") return a.pastorId === currentUser.id;
        return true;
      })
      .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [appointments, filter, currentUser.id]);

  const save = async (data) => {
    try {
      if (editAppt) {
        const updated = await apiUpdateAppointment(editAppt.id, data);
        setAppointments(prev => prev.map(x => x.id === editAppt.id ? updated : x));
      } else {
        const created = await apiCreateAppointment(data);
        setAppointments(prev => [...prev, created]);
      }
      setEditAppt(null);
      return true;
    } catch (e) { alert(e.message || "Could not save"); return false; }
  };
  const setStatus = async (a, status) => {
    try {
      const updated = await apiSetAppointmentStatus(a.id, status);
      setAppointments(prev => prev.map(x => x.id === a.id ? updated : x));
    } catch (e) { alert(e.message || "Could not update"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try {
      await apiDeleteAppointment(id);
      setAppointments(prev => prev.filter(x => x.id !== id));
    } catch (e) { alert(e.message || "Could not delete"); }
  };

  const userNameById = (id) => {
    if (!id) return "—";
    const u = users.find(x => x.id === id);
    return u?.name || u?.username || id;
  };

  return (
    <div data-testid="appointments-page">
      <div style={{ marginBottom: 12 }}>
        <StructuralBackButton to="/browse" label="← Browse" testId="appt-manage-back" />
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-text)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Appointments {isSuperAdmin && <span style={{ fontSize: 11, color: "var(--kc-accent)", letterSpacing: "0.08em" }}>· master overview</span>}
        </div>
        <button className="btn btn-gold" data-testid="appt-new-btn" onClick={() => { setEditAppt(null); setShowForm(true); }}>+ New Appointment</button>
      </div>

      <div className="checkins-tabs" role="tablist" style={{ marginBottom: 14 }}>
        <button type="button" data-testid="appt-filter-upcoming" className={`checkins-tab ${filter === "upcoming" ? "active" : ""}`} onClick={() => setFilter("upcoming")}>Upcoming</button>
        <button type="button" data-testid="appt-filter-mine" className={`checkins-tab ${filter === "mine" ? "active" : ""}`} onClick={() => setFilter("mine")}>Booked by me</button>
        <button type="button" data-testid="appt-filter-inbound" className={`checkins-tab ${filter === "inbound" ? "active" : ""}`} onClick={() => setFilter("inbound")}>With me</button>
        <button type="button" data-testid="appt-filter-admin-tasks" className={`checkins-tab ${filter === "admin-tasks" ? "active" : ""}`} onClick={() => setFilter("admin-tasks")}>Administrative Tasks</button>
        <button type="button" data-testid="appt-filter-all" className={`checkins-tab ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>All</button>
      </div>

      {filter === "admin-tasks" ? (
        <AdminTasksList
          currentUser={currentUser}
          isSuperAdmin={isSuperAdmin}
          appointments={appointments}
          setAppointments={setAppointments}
          users={users}
          pastorAssistantsById={pastorAssistantsById}
        />
      ) : visible.length === 0 ? (
        <div className="widget-empty-add">
          <div className="wea-icon"><CustomIcon name="calendar-clock" size={40} /></div>
          <div className="wea-title">No appointments yet</div>
          <div className="wea-sub">Tap &ldquo;+ New Appointment&rdquo; to schedule one. Confirmed appointments will also appear in the Tasks → Appointments tab.</div>
        </div>
      ) : (
        <div>
          {visible.map(a => {
            const d = a.date ? new Date(a.date) : null;
            const otherIsPastor = a.userId === currentUser.id;
            const otherUser = otherIsPastor ? a.pastorId : a.userId;
            const otherName = otherIsPastor ? userNameById(a.pastorId) : (a.userName || userNameById(a.userId));
            const canConfirm = a.pastorId === currentUser.id && a.status === "pending";
            const canEdit = a.userId === currentUser.id || isSuperAdmin;
            return (
              <div key={a.id} className="appt-row" data-testid={`apptmgr-row-${a.id}`}>
                <div className="appt-row-top">
                  <div className="appt-date-box">
                    <div className="a-month">{d ? d.toLocaleDateString("en-US", { month: "short" }) : "—"}</div>
                    <div className="a-day">{d ? d.getDate() : "—"}</div>
                  </div>
                  <div className="appt-main">
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <div className="appt-title" style={{ flex: 1, minWidth: 0 }}>{a.title || "Appointment"}</div>
                      <span className={`appt-status ${a.status}`}>{a.status}</span>
                    </div>
                    <div className="appt-meta">
                      {a.time ? `${a.time} · ` : ""}With {otherName} {otherUser ? "" : "(personal)"}
                    </div>
                    {a.reason && <div className="appt-meta" style={{ marginTop: 2 }}>{a.reason}</div>}
                  </div>
                </div>
                <div className="appt-row-actions">
                  {canConfirm && <button className="btn btn-sm btn-gold" data-testid={`apptmgr-confirm-${a.id}`} onClick={() => setStatus(a, "confirmed")}>Confirm</button>}
                  {a.status !== "cancelled" && canEdit && (
                    <button className="btn btn-sm btn-outline" data-testid={`apptmgr-cancel-${a.id}`} onClick={() => { if (window.confirm("Cancel this appointment?")) setStatus(a, "cancelled"); }}>Cancel</button>
                  )}
                  {canEdit && (
                    <button className="btn btn-sm btn-outline" data-testid={`apptmgr-edit-${a.id}`} onClick={() => { setEditAppt(a); setShowForm(true); }}>Edit</button>
                  )}
                  {canEdit && (
                    <button className="btn btn-sm btn-danger" data-testid={`apptmgr-delete-${a.id}`} onClick={() => remove(a.id)}>Delete</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <AppointmentFormModal
          existing={editAppt || (prefillDate ? { date: prefillDate } : null)}
          currentUser={currentUser}
          users={users}
          onSave={save}
          onClose={() => { setShowForm(false); setEditAppt(null); setPrefillDate(""); }}
        />
      )}
    </div>
  );
}
